import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { tournaments } from "../../lib/tournamentApi";
import { GameRow, groupRounds } from "./BracketTab";
import { inputStyle } from "./shared";

export default function PoolsPanel({ data, division, isDirector, onChanged, onEdit, teamsById }) {
  const t = data.tournament;
  const pools = data.pools.filter((p) => p.division_id === division.id);
  const [count, setCount] = useState(division.pool_config?.pool_count || 2);
  const [table, setTable] = useState(null);
  const [preview, setPreview] = useState(null);
  const inPools = division.stage === "pools";
  const name = (id) => teamsById[id]?.name || "TBD";

  useEffect(() => {
    if (!division.bracket_locked) return;
    tournaments.standings(t.id, division.id).then(setTable).catch(() => {});
  }, [t.id, division.id, division.bracket_locked, data.games]);

  async function run(fn, ok) {
    try { const r = await fn(); if (ok) toast.success(ok); await onChanged(); return r; }
    catch (e) { toast.error(e.message || "Couldn't save."); return null; }
  }
  async function loadPreview() {
    try { setPreview(await tournaments.seedPreview(t.id, division.id)); } catch (e) { toast.error(e.message); }
  }

  if (!division.bracket_locked) {
    return (
      <div data-testid="pools-setup">
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="number" min={1} max={16} value={count} onChange={(e) => setCount(Number(e.target.value))}
                 data-testid="pools-count" style={{ ...inputStyle, width: 80 }} />
          <ChalkActionButton label="Generate pools (snake by seed)" fullWidth={false} testId="pools-generate"
                             onClick={() => run(() => tournaments.generatePools(t.id, division.id, count), "Pools generated.")} />
        </div>
        {pools.map((p) => (
          <ChalkCard key={p.id} data-testid={`pool-${p.id}`} style={{ marginTop: 8 }}>
            <b>{p.name}</b>
            {p.team_ids.map((tid) => (
              <div key={tid} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginTop: 4 }}>
                <span style={{ flex: 1 }}>{name(tid)}</span>
                <select value={p.id} data-testid={`pool-move-${tid}`} style={{ ...inputStyle, width: 110, padding: "4px 6px" }}
                        onChange={(e) => run(() => tournaments.movePoolTeam(t.id, division.id, { team_id: tid, pool_id: e.target.value }))}>
                  {pools.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
            ))}
          </ChalkCard>
        ))}
        {pools.length > 0 && isDirector && (
          <div style={{ marginTop: 10 }}>
            <ChalkActionButton variant="primary" label="Lock pools & create games" testId="pools-lock"
                               onClick={() => run(() => tournaments.lockPools(t.id, division.id), "Pool games created.")} />
          </div>
        )}
      </div>
    );
  }

  const poolGames = data.games.filter((g) => g.division_id === division.id && g.stage === "pool");
  return (
    <div data-testid="pools-live">
      {(table?.pools || []).map((p) => (
        <div key={p.pool_id} style={{ marginBottom: 14 }}>
          <SectionTitle label={`${p.name}${p.rows[0]?.provisional ? " · provisional" : ""}`} />
          <StandingsTable rows={p.rows} name={name} testId={`standings-${p.pool_id}`} />
          {groupRounds(poolGames.filter((g) => g.pool_id === p.pool_id)).roundKeys.length > 0 && (
            <details style={{ marginTop: 6 }}><summary style={{ fontSize: 12, cursor: "pointer" }}>Games</summary>
              {poolGames.filter((g) => g.pool_id === p.pool_id).map((g) => (
                <GameRow key={g.id} game={g} teamsById={teamsById} tz={t.timezone} onClick={() => onEdit(g)} />
              ))}
            </details>
          )}
        </div>
      ))}
      {inPools && isDirector && (
        <div data-testid="seed-preview-block">
          <ChalkActionButton label="Preview bracket seeding" variant="ghost" testId="seed-preview-load" onClick={loadPreview} />
          {preview && <SeedPreview preview={preview} name={name} />}
          {preview?.complete && (
            <div style={{ marginTop: 8 }}>
              <ChalkActionButton variant="primary" label="Lock seeding & build Gold/Silver" testId="pools-advance"
                                 onClick={() => run(() => tournaments.advancePools(t.id, division.id), "Brackets built.")} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function StandingsTable({ rows, name, testId }) {
  const th = { textAlign: "right", padding: "4px 6px", fontSize: 10, color: "var(--chalk-muted)" };
  const td = { textAlign: "right", padding: "4px 6px", fontSize: 12 };
  return (
    <div style={{ overflowX: "auto" }}>
      <table data-testid={testId} style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><th style={{ ...th, textAlign: "left" }}>#</th><th style={{ ...th, textAlign: "left" }}>Team</th>
          {["W", "L", "T", "PTS", "DIFF", "PA", "PF"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.team_id} style={{ borderTop: "0.5px solid var(--chalk-border)" }}>
              <td style={{ ...td, textAlign: "left" }}>{r.place}</td>
              <td style={{ ...td, textAlign: "left" }}>{name(r.team_id)}
                {r.note && <div style={{ fontSize: 10, color: "var(--chalk-muted)" }}>{r.note}</div>}</td>
              {[r.w, r.l, r.t, r.pts, r.diff, r.pa, r.pf].map((v, i) => <td key={i} style={td}>{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeedPreview({ preview, name }) {
  return ["gold", "silver"].map((k) => preview[k] && (
    <div key={k} data-testid={`seed-preview-${k}`} style={{ marginTop: 10 }}>
      <SectionTitle label={`${k === "gold" ? "Gold" : "Silver"} seeds`} />
      <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>{preview[k].seed_order.map((id) => <li key={id}>{name(id)}</li>)}</ol>
      {preview[k].swaps.length > 0 && <div style={{ fontSize: 11, color: "var(--chalk-muted)" }}>
        Swapped seeds {preview[k].swaps.map((s) => s.seeds.join("↔")).join(", ")} to avoid same-pool first-round games.</div>}
      {preview[k].unavoidable_same_pool.map((p, i) => (
        <Warn key={`u${i}`} testId={`seed-unavoidable-${k}-${i}`}>Same-pool first-round game couldn't be avoided: {name(p[0])} vs {name(p[1])}</Warn>
      ))}
      {preview[k].possible_rematches.map((p, i) => (
        <Warn key={`r${i}`} testId={`seed-rematch-${k}-${i}`}>Possible round {p.round} rematch: {name(p.teams[0])} vs {name(p.teams[1])}</Warn>
      ))}
    </div>
  ));
}

function Warn({ children, testId }) {
  return <div data-testid={testId} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: "var(--chalk-amber)", marginTop: 4 }}>
    <AlertTriangle size={13} aria-hidden="true" /> {children}</div>;
}
